import { useEffect } from 'react';

import type { KeyedMutator } from 'swr';

import useMedias from '@/utils/hooks/swr/useMedias';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { X } from '@/lib/socialProfile';

import PostsTab from '@/components/pages/myprofile/PostsTab';

type MediaType = {
    id: string;
    labels: {
        label: string;
    }[];
    albums: {
        id: number;
        count_medias: number;
        name: string;
        type: 'auto' | 'manual';
    }[];
};

type RequestParams = {
    sort?: { column?: string };
    filter_post_type?: string[];
    with_labels?: boolean;
    with_albums?: boolean;
    social_profile_ids?: string[];
    [key: string]: unknown;
} | null;

type SetRequestParams = (params: RequestParams | ((prev: RequestParams) => RequestParams)) => void;

export type BaseXTabProps = {
    isFiltersOpen?: boolean;
    toggleFilters?: () => void;
    isExportModalOpen?: boolean;
    toggleExportModal?: () => void;
    header?: (setRequestParams: SetRequestParams | undefined, postType?: string) => React.ReactNode;
    /** Additional initial params to merge with base params */
    initialParams?: Record<string, unknown>;
    /** Render prop for custom filters */
    renderFilters?: (
        resetFilters: boolean,
        setResetFilters: (value: boolean) => void,
        setRequestParams: SetRequestParams
    ) => React.ReactNode;
    /** Export type for the export modal */
    exportType?: string;
    /** Whether this tab has filters. Defaults to true */
    hasFilter?: boolean;
};

export const BaseXTab = ({
    isFiltersOpen,
    toggleFilters,
    isExportModalOpen,
    toggleExportModal,
    header,
    initialParams = {},
    renderFilters,
    exportType,
    hasFilter = true
}: BaseXTabProps) => {
    const { activeSocialProfile } = useActiveSocialProfile();

    const {
        dataMedias,
        requestParams,
        isLoadMore,
        isLoadingMore,
        isLoadingInitialData,
        mutateMedias,
        setRequestParams,
        loadMoreMedia,
        isMediasV2
    } = useMedias({
        activeSPID: activeSocialProfile?.id,
        network: X
    }) as {
        dataMedias: Record<'medias' | 'stories', MediaType[]>[] | undefined;
        requestParams: RequestParams;
        isLoadMore: boolean;
        isLoadingMore: boolean;
        isLoadingInitialData: boolean;
        mutateMedias: KeyedMutator<Record<'medias' | 'stories', MediaType[]>[]>;
        setRequestParams: SetRequestParams;
        loadMoreMedia: () => void;
        isMediasV2: boolean;
    };

    // Set initial params - for v2, include social_profile_ids
    useEffect(() => {
        const params = { with_labels: true, with_albums: true, ...initialParams };

        if (isMediasV2) {
            if (!activeSocialProfile?.id) return;
            setRequestParams((prevState) => ({
                ...prevState,
                ...params,
                social_profile_ids: [activeSocialProfile.id.toString()]
            }));
        } else {
            setRequestParams((prevState) => ({ ...prevState, ...params }));
        }
    }, [setRequestParams, isMediasV2, activeSocialProfile?.id]);

    // For v2, guard against rendering when no social profile is selected
    if (isMediasV2 && !activeSocialProfile?.id) {
        return null;
    }

    // Wrap renderFilters to inject setRequestParams
    const wrappedRenderFilters = renderFilters
        ? (resetFilters: boolean, setResetFilters: (value: boolean) => void) =>
              renderFilters(resetFilters, setResetFilters, setRequestParams)
        : undefined;

    return (
        <PostsTab
            isFiltersOpen={isFiltersOpen}
            toggleFilters={toggleFilters}
            isExportModalOpen={isExportModalOpen}
            toggleExportModal={toggleExportModal}
            networkType={activeSocialProfile?.social_network_type ?? X}
            dataMedias={dataMedias}
            isLoadMore={isLoadMore}
            isLoadingMore={isLoadingMore}
            isLoadingInitialData={isLoadingInitialData}
            requestParams={requestParams}
            setRequestParams={setRequestParams}
            mutateMedias={mutateMedias}
            loadMoreMedia={loadMoreMedia}
            header={header}
            exportType={exportType}
            renderFilters={hasFilter ? wrappedRenderFilters : undefined}
        />
    );
};
